import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  RotateCcw, Search, Plus, 
  Package, Clock, CheckCircle2, 
  MoreVertical, Eye, Edit3, FileCheck, Ban, FileText,
  X, AlertCircle, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { returnService } from '../../../../services/erpApi';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import './ReturnsPage.css';

interface ReturnRequest {
  id: number;
  type: 'exchange' | 'return';
  customer_id: number;
  customer_name?: string;
  status: 'pending' | 'collected' | 'in_inspection' | 'approved' | 'rejected' | 'cancelled';
  total_amount: number;
  observation?: string;
  created_at: string;
  fiscal_return_eligible?: boolean;
  original_seller_id?: number;
  sale_id?: number;
  items?: Array<{
    id: number;
    product_id: number;
    product_name?: string;
    quantity: number;
    unit_price: number;
    reason?: string;
  }>;
}

const ReturnsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'collected' | 'in_inspection' | 'approved' | 'rejected'>('all');
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showNfeReturnModal, setShowNfeReturnModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedReturnId, setSelectedReturnId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const canView = hasPermission(MODULES.STOCK, ACTIONS.STOCK_RETURNS_VIEW);
  const canCreate = hasPermission(MODULES.STOCK, ACTIONS.STOCK_RETURNS_CREATE);
  const canApprove = hasPermission(MODULES.STOCK, ACTIONS.STOCK_RETURNS_APPROVE);
  const canUpdate = hasPermission(MODULES.STOCK, ACTIONS.STOCK_RETURNS_UPDATE);

  const fetchReturns = useCallback(async () => {
    if (!canView) return;
    
    setLoading(true);
    try {
      const filters: any = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const response = await returnService.getAll(filters);
      
      if (response.success) {
        setReturns(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar devoluções:', error);
    } finally {
      setLoading(false);
    }
  }, [canView, statusFilter, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lógica de Filtragem Combinada
  const filteredReturns = useMemo(() => {
    if (!searchTerm) return returns;
    
    return returns.filter(ret => {
      const searchLower = searchTerm.toLowerCase();
      return (
        ret.customer_name?.toLowerCase().includes(searchLower) ||
        `RT-${ret.id}`.toLowerCase().includes(searchLower) ||
        ret.observation?.toLowerCase().includes(searchLower) ||
        ret.items?.some(item => item.product_name?.toLowerCase().includes(searchLower))
      );
    });
  }, [returns, searchTerm]);

  const handleApproveClick = (id: number) => {
    if (!canApprove) return;
    setSelectedReturnId(id);
    setShowApproveModal(true);
    setOpenDropdownId(null);
  };

  const handleApproveConfirm = async () => {
    if (!selectedReturnId) return;
    
    setShowApproveModal(false);

    try {
      const response = await returnService.approve(selectedReturnId);
      if (response.success) {
        await fetchReturns();
      } else {
        alert(response.message || 'Erro ao aprovar devolução');
      }
    } catch (error) {
      console.error('Erro ao aprovar devolução:', error);
      alert('Erro ao aprovar devolução. Tente novamente.');
    } finally {
      setSelectedReturnId(null);
    }
  };

  const handleRejectClick = (id: number) => {
    if (!canApprove) return;
    setSelectedReturnId(id);
    setRejectReason('');
    setShowRejectModal(true);
    setOpenDropdownId(null);
  };

  const handleRejectConfirm = async () => {
    if (!selectedReturnId || !rejectReason.trim()) {
      alert('Por favor, informe o motivo da rejeição.');
      return;
    }
    
    setShowRejectModal(false);

    try {
      const response = await returnService.reject(selectedReturnId, rejectReason.trim());
      if (response.success) {
        await fetchReturns();
      } else {
        alert(response.message || 'Erro ao rejeitar devolução');
      }
    } catch (error) {
      console.error('Erro ao rejeitar devolução:', error);
      alert('Erro ao rejeitar devolução. Tente novamente.');
    } finally {
      setSelectedReturnId(null);
      setRejectReason('');
    }
  };

  const handleCancelClick = (id: number) => {
    if (!canUpdate) return;
    setSelectedReturnId(id);
    setShowCancelModal(true);
    setOpenDropdownId(null);
  };

  const handleCancelConfirm = async () => {
    if (!selectedReturnId) return;
    
    setShowCancelModal(false);

    try {
      const response = await returnService.delete(selectedReturnId);
      if (response.success) {
        await fetchReturns();
      } else {
        alert(response.message || 'Erro ao cancelar devolução');
      }
    } catch (error) {
      console.error('Erro ao cancelar devolução:', error);
      alert('Erro ao cancelar devolução. Tente novamente.');
    } finally {
      setSelectedReturnId(null);
    }
  };

  const stats = [
    { 
      label: 'Pendentes de Coleta', 
      value: returns.filter(r => r.status === 'pending').length, 
      iconClass: 'pending'
    },
    { 
      label: 'Em Inspeção', 
      value: returns.filter(r => r.status === 'in_inspection').length, 
      iconClass: 'inspection'
    },
    { 
      label: 'Aprovadas', 
      value: returns.filter(r => r.status === 'approved').length, 
      iconClass: 'approved'
    },
    { 
      label: 'Total de Devoluções', 
      value: returns.length, 
      iconClass: 'total'
    },
  ];

  const getStatusStyle = (status: string) => {
    // Retorna a classe CSS customizada para o status
    switch (status) {
      case 'approved': return 'approved';
      case 'pending': return 'pending';
      case 'collected': return 'collected';
      case 'in_inspection': return 'in_inspection';
      case 'rejected': return 'rejected';
      case 'cancelled': return 'cancelled';
      default: return 'pending';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return 'Aprovada';
      case 'pending': return 'Aguardando';
      case 'collected': return 'Coletada';
      case 'in_inspection': return 'Em Inspeção';
      case 'rejected': return 'Rejeitada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  if (!canView) {
    return (
      <div className="returns-page">
        <div className="access-denied">
          <AlertCircle size={48} />
          <h2>Acesso Negado</h2>
          <p>Você não tem permissão para visualizar devoluções.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="returns-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>
            Logística <span style={{ color: '#4f46e5' }}>Reversa</span>
          </h1>
          <p>Trocas, Devoluções e Gestão de Créditos B2B</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
            className="btn-clear"
          >
            <RotateCcw size={16} /> Limpar
          </button>
          {canCreate && (
            <button 
              onClick={() => navigate('/erp/estoque/trocas/nova')}
              className="btn-new"
            >
              <Plus size={16} /> Nova Solicitação
            </button>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="stats-grid">
        {stats.map((s, i) => (
          <div key={i} className={`stat-card ${s.iconClass}`}>
            <div className="stat-card-content">
              <div className={`stat-icon-wrapper ${s.iconClass}`}>
                <RotateCcw size={18} />
              </div>
              <p className="stat-label">{s.label}</p>
              <p className="stat-value">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Container */}
      <div className="main-container">
        {/* Active Filters & Search */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="search-wrapper">
              <Search className="search-icon" size={16} />
              <input 
                type="text" 
                placeholder="Filtrar por Cliente, ID ou Produto..." 
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="btn-clear-search">
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="status-filters scrollbar-hide">
              <span className="status-filters-label">Status:</span>
              {[
                { id: 'all', label: 'Todos' },
                { id: 'pending', label: 'Aguardando' },
                { id: 'collected', label: 'Coletada' },
                { id: 'in_inspection', label: 'Inspeção' },
                { id: 'approved', label: 'Aprovada' },
                { id: 'rejected', label: 'Rejeitada' }
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id as any)}
                  className={`status-filter-btn ${statusFilter === f.id ? 'active' : ''}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <Loader2 className="loading-spinner animate-spin" size={32} />
              <p className="loading-text">Carregando devoluções...</p>
            </div>
          ) : filteredReturns.length > 0 ? (
            <table className="returns-table">
              <thead>
                <tr>
                  <th>Solicitação / Cliente</th>
                  <th>Produtos</th>
                  <th>Valor Crédito</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredReturns.map((ret) => (
                  <tr key={ret.id}>
                    <td>
                      <div>
                        <p className="return-id">RT-{ret.id}</p>
                        <p className="return-client">{ret.customer_name || 'Cliente não informado'}</p>
                        <p className="return-client" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{formatDate(ret.created_at)}</p>
                      </div>
                    </td>
                    <td>
                      <div className="product-info">
                        <div className="product-icon">
                          <Package size={18} />
                        </div>
                        <div className="product-details">
                          <p className="product-name">
                            {ret.items?.length || 0} {ret.items?.length === 1 ? 'item' : 'itens'}
                          </p>
                          {ret.observation && (
                            <p className="product-reason">{ret.observation}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className="return-value">
                        {formatCurrency(ret.total_amount || 0)}
                      </p>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`status-badge ${getStatusStyle(ret.status)}`}>
                        {ret.status === 'approved' ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                        {getStatusLabel(ret.status)}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => setOpenDropdownId(openDropdownId === ret.id ? null : ret.id)}
                        className={`btn-actions ${openDropdownId === ret.id ? 'active' : ''}`}
                      >
                        <MoreVertical size={18} />
                      </button>

                      {openDropdownId === ret.id && (
                        <div ref={dropdownRef} className="actions-dropdown">
                          <button 
                            onClick={() => {
                              navigate(`/erp/estoque/trocas/${ret.id}`);
                              setOpenDropdownId(null);
                            }}
                            className="dropdown-item"
                          >
                            <Eye size={14} style={{ color: '#4f46e5' }} /> Visualizar Protocolo
                          </button>
                          {canUpdate && (
                            <button 
                              onClick={() => {
                                navigate(`/erp/estoque/trocas/${ret.id}/editar`);
                                setOpenDropdownId(null);
                              }}
                              className="dropdown-item"
                            >
                              <Edit3 size={14} style={{ color: '#3b82f6' }} /> Editar Solicitação
                            </button>
                          )}
                          
                          {canApprove && (ret.status === 'pending' || ret.status === 'collected' || ret.status === 'in_inspection') && (
                            <button 
                              onClick={() => handleApproveClick(ret.id)}
                              className="dropdown-item"
                              style={{ color: '#10b981' }}
                            >
                              <FileCheck size={14} /> Aprovar Devolução
                            </button>
                          )}

                          {canApprove && ret.status === 'pending' && (
                            <button 
                              onClick={() => handleRejectClick(ret.id)}
                              className="dropdown-item"
                              style={{ color: '#ef4444' }}
                            >
                              <Ban size={14} /> Rejeitar
                            </button>
                          )}

                          {canApprove && ret.status === 'approved' && ret.fiscal_return_eligible && (
                            <button
                              onClick={() => {
                                setSelectedReturnId(ret.id);
                                setShowNfeReturnModal(true);
                                setOpenDropdownId(null);
                              }}
                              className="dropdown-item"
                            >
                              <FileText size={14} style={{ color: '#7c3aed' }} /> Emitir NF Devolução
                            </button>
                          )}

                          {canUpdate && ret.status !== 'approved' && (
                            <>
                              <div className="dropdown-divider" />
                              <button 
                                onClick={() => handleCancelClick(ret.id)}
                                className="dropdown-item danger"
                              >
                                <Ban size={14} /> Cancelar Protocolo
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <AlertCircle size={40} />
              </div>
              <h3 className="empty-title">Nenhum protocolo encontrado</h3>
              <p className="empty-message">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Tente ajustar seus filtros ou termos de busca para localizar o registro.'
                  : 'Nenhuma devolução foi registrada ainda.'}
              </p>
              {(searchTerm || statusFilter !== 'all') && (
                <button 
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                  className="btn-reset-filters"
                >
                  Resetar Filtros
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <p className="pagination-info">
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
            </p>
            <div className="pagination-controls">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="btn-pagination"
              >
                Anterior
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn-pagination"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Aprovação */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setSelectedReturnId(null);
        }}
        onConfirm={handleApproveConfirm}
        title="Aprovar Devolução"
        message={
          <div>
            <p style={{ marginBottom: '1rem' }}>
              Deseja realmente aprovar esta devolução?
            </p>
            <div style={{ 
              background: 'var(--bg-secondary, #f9fafb)', 
              padding: '1rem', 
              borderRadius: '1rem',
              fontSize: '0.875rem'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>Ao aprovar, será realizado:</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                <li>Os produtos retornarão ao estoque</li>
                <li>O histórico de movimentação será registrado</li>
                <li>Um crédito será gerado para o cliente</li>
              </ul>
            </div>
          </div>
        }
        type="success"
        confirmText="Aprovar"
        cancelText="Cancelar"
        showCancel={true}
      />

      {/* Modal de Confirmação de Rejeição */}
      <ConfirmModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedReturnId(null);
          setRejectReason('');
        }}
        onConfirm={handleRejectConfirm}
        title="Rejeitar Devolução"
        message={
          <div>
            <p style={{ marginBottom: '1rem' }}>
              Informe o motivo da rejeição:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Digite o motivo da rejeição..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '0.75rem',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color, #e5e7eb)',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
        }
        type="danger"
        confirmText="Rejeitar"
        cancelText="Cancelar"
        showCancel={true}
      />

      <ConfirmModal
        isOpen={showNfeReturnModal}
        onClose={() => {
          setShowNfeReturnModal(false);
          setSelectedReturnId(null);
        }}
        onConfirm={() => {
          setShowNfeReturnModal(false);
          setSelectedReturnId(null);
        }}
        title="NF-e de Devolução"
        message="A emissão de Nota Fiscal de Devolução de Venda será disponibilizada em breve. A devolução operacional, crédito do cliente e estorno de faturamento já foram registrados."
        type="info"
        confirmText="Entendi"
        cancelText=""
        showCancel={false}
      />

      {/* Modal de Confirmação de Cancelamento */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedReturnId(null);
        }}
        onConfirm={handleCancelConfirm}
        title="Cancelar Protocolo"
        message="Deseja realmente cancelar este protocolo de devolução? Esta ação não pode ser desfeita."
        type="danger"
        confirmText="Cancelar Protocolo"
        cancelText="Voltar"
        showCancel={true}
      />
    </div>
  );
};

export default ReturnsPage;
